<template>
    <div class="h-full flex gap-6">
        <!-- Left Side: Filters -->
        <div class="w-[400px] flex-shrink-0 pl-6 py-6 flex flex-col gap-4 overflow-y-auto">
            <!-- 날짜 선택 -->
            <DateSelector v-model="selectedDate" @dateSelected="handleDateChange" class="flex-shrink-0" />

            <!-- 분류 기준 선택 -->
            <GroupTypeSelector v-model="groupType" @update:modelValue="handleGroupTypeChange" />

            <!-- 섹터별 선택 (채권 유형 그룹일 때만 표시) -->
            <Transition name="fade" mode="out-in">
                <SectorTypeSelector v-if="groupType === 'BOND_TYPE'" :key="'sector'" v-model="selectedCreditType"
                    @update:modelValue="handleCreditTypeChange" @group-selected="handleBondGroupChange" />

                <!-- 기관별 선택 (기관 유형 그룹일 때만 표시) -->
                <InstitutionTypeSelector v-else :key="'institution'" v-model="selectedInstitution"
                    @update:modelValue="handleInstitutionChange" />
            </Transition>

            <!-- 수급 방향 필터 -->
            <DirectionFilter v-model="direction" @update:modelValue="handleDirectionChange" />

            <!-- 시장 유형 필터 -->
            <MarketTypeFilter v-model="market" @update:modelValue="handleMarketChange" />
        </div>

        <!-- Right Side: Tables -->
        <div class="flex-1 pr-6 py-6 overflow-y-auto">
            <!-- 로딩 상태 -->
            <div v-if="loading" class="flex items-center justify-center h-full">
                <div class="text-center">
                    <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <p class="mt-4 text-gray-600">데이터를 불러오는 중...</p>
                </div>
            </div>

            <!-- 에러 상태 -->
            <div v-else-if="error" class="flex items-center justify-center h-full">
                <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-red-500" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-red-600 font-medium">데이터를 불러오는데 실패했습니다</p>
                    <p class="text-gray-500 text-sm mt-2">{{ error.message }}</p>
                </div>
            </div>

            <!-- 데이터 표시 -->
            <div v-else class="flex flex-col gap-6">
                <!-- 수급 현황 테이블 -->
                <div class="flex-shrink-0">
                    <SupplyDemandTable :table-data="summaryTableData" :group-type="groupType"
                        :selected-cell="selectedCell" :direction-filter="direction" @cell-click="handleCellClick" />
                </div>

                <!-- 수급 상세 테이블 -->
                <div class="flex-shrink-0">
                    <DetailTable :records="detailTableData" :selected-cell="selectedCell" :group-type="groupType"
                        :direction-filter="direction" :selected-institution="selectedInstitution" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import DateSelector from '@/components/NavigationDateSelector.vue';
import GroupTypeSelector from '@/components/marketFlow/GroupTypeSelector.vue';
import SectorTypeSelector from '@/components/marketFlow/SectorTypeSelector.vue';
import InstitutionTypeSelector from '@/components/marketFlow/InstitutionTypeSelector.vue';
import DirectionFilter from '@/components/marketFlow/DirectionFilter.vue';
import MarketTypeFilter from '@/components/marketFlow/MarketTypeFilter.vue';
import SupplyDemandTable from '@/components/marketFlow/SupplyDemandTable.vue';
import DetailTable from '@/components/marketFlow/DetailTable.vue';
import { getPreviousBusinessDayStr } from '@/utils/util';
import userService from '@/service/userService';
import { useMarketFlow } from '@/composables/useMarketFlow';

export default {
    name: 'MarketFlowView',
    components: {
        DateSelector,
        GroupTypeSelector,
        SectorTypeSelector,
        InstitutionTypeSelector,
        DirectionFilter,
        MarketTypeFilter,
        SupplyDemandTable,
        DetailTable,
    },
    setup() {
        const selectedDate = ref(getPreviousBusinessDayStr());

        // Market Flow Composable 사용
        const {
            loading,
            error,
            groupType,
            selectedCreditType,
            selectedInstitution,
            direction,
            market,
            selectedCell,
            summaryTableData,
            detailTableData,
            fetchRecords,
            selectCell,
            changeGroupType,
            changeCreditType,
            changeInstitution,
            changeBondGroup,
            updateDirectionFilter,
            updateMarketFilter,
        } = useMarketFlow();

        // 페이지 가시성 관련 변수들
        let healthCheckInterval = null;
        const HEALTH_CHECK_INTERVAL = 5000; // 5초

        // 날짜 변경 핸들러
        const handleDateChange = async (date) => {
            selectedDate.value = date;
            await fetchRecords(date);
        };

        // 그룹 타입 변경 핸들러
        const handleGroupTypeChange = (newGroupType) => {
            changeGroupType(newGroupType);
        };

        // 채권 유형 변경 핸들러
        const handleCreditTypeChange = (newCreditType) => {
            changeCreditType(newCreditType);
        };

        // 채권 그룹 변경 핸들러
        const handleBondGroupChange = (newGroup) => {
            changeBondGroup(newGroup);
        };

        // 기관 유형 변경 핸들러
        const handleInstitutionChange = (newInstitution) => {
            changeInstitution(newInstitution);
        };

        // 방향 필터 변경 핸들러
        const handleDirectionChange = (newDirection) => {
            updateDirectionFilter(newDirection);
        };

        // 시장 필터 변경 핸들러
        const handleMarketChange = (newMarket) => {
            updateMarketFilter(newMarket);
        };

        // 셀 클릭 핸들러
        const handleCellClick = ({ rowKey, colKey }) => {
            selectCell(rowKey, colKey);
        };

        // 페이지 가시성 변경 감지 핸들러
        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (healthCheckInterval) {
                    clearInterval(healthCheckInterval);
                    healthCheckInterval = null;
                }
            } else {
                startHealthCheck();
            }
        };

        // health check 시작 함수
        const startHealthCheck = () => {
            if (healthCheckInterval) return;
            sendHealthCheck();
            healthCheckInterval = setInterval(() => {
                sendHealthCheck();
            }, HEALTH_CHECK_INTERVAL);
        };

        // health check 전송 함수
        const sendHealthCheck = async () => {
            if (!document.hidden) {
                try {
                    const currentPath = window.location.pathname;
                    await userService.sendHealthCheck(currentPath);
                } catch (error) {
                    console.error('Health check 전송 실패:', error);
                }
            }
        };

        onMounted(async () => {
            // 초기 데이터 로드
            await fetchRecords(selectedDate.value);

            // 페이지 가시성 변경 이벤트 리스너 등록
            document.addEventListener('visibilitychange', handleVisibilityChange);

            // 초기 시작
            if (!document.hidden) {
                startHealthCheck();
            }
        });

        onUnmounted(() => {
            // 이벤트 리스너 제거 및 인터벌 정리
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (healthCheckInterval) {
                clearInterval(healthCheckInterval);
            }
        });

        return {
            selectedDate,
            loading,
            error,
            groupType,
            selectedCreditType,
            selectedInstitution,
            direction,
            market,
            selectedCell,
            summaryTableData,
            detailTableData,
            handleDateChange,
            handleGroupTypeChange,
            handleCreditTypeChange,
            handleBondGroupChange,
            handleInstitutionChange,
            handleDirectionChange,
            handleMarketChange,
            handleCellClick,
        };
    },
};
</script>

<style scoped>
/* 스크롤바 스타일링 */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Transition 애니메이션 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from {
    opacity: 0;
    transform: translateX(-10px);
}

.fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}
</style>
